import { z } from "zod";
import { timestampSchema } from "./common.js";

export enum SessionStatus {
  RUNNING = "running",
  COMPLETED = "completed",
  STOPPED = "stopped",
  ERROR = "error",
}

export const sessionStatusSchema = z.nativeEnum(SessionStatus);

export interface TestSession {
  [key: string]: unknown;
  id: string;
  projectId: string;
  scenarioId?: string;
  simulationTestId?: string;
  connectionProfileId?: string;
  environment: string;
  startedAt: string;
  endedAt?: string;
  status: SessionStatus;
  messageCount: number;
  passCount: number;
  warnCount: number;
  failCount: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export const testSessionSchema: z.ZodSchema<TestSession> = z
  .object({
    id: z.string(),
    projectId: z.string(),
    scenarioId: z.string().optional(),
    simulationTestId: z.string().optional(),
    connectionProfileId: z.string().optional(),
    environment: z.string(),
    startedAt: timestampSchema,
    endedAt: timestampSchema.optional(),
    status: sessionStatusSchema,
    messageCount: z.number().int(),
    passCount: z.number().int(),
    warnCount: z.number().int(),
    failCount: z.number().int(),
    createdBy: z.string(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .passthrough();

export enum SessionEvidenceType {
  VALIDATION = "validation",
  ERROR = "error",
  GIS = "gis",
  REPLAY = "replay",
  SCREENSHOT = "screenshot",
  RAW_MESSAGE = "RAW_MESSAGE",
  PARSED_MESSAGE = "PARSED_MESSAGE",
  FIELD_ERROR = "FIELD_ERROR",
  ROLE_ERROR = "ROLE_ERROR",
  TIMING_ERROR = "TIMING_ERROR",
  SEQUENCE_ERROR = "SEQUENCE_ERROR",
  GIS_POSITION = "GIS_POSITION",
  ALERT = "ALERT",
  SYSTEM_ERROR = "SYSTEM_ERROR",
  SCREENSHOT_REFERENCE = "SCREENSHOT_REFERENCE",
}

export const sessionEvidenceTypeSchema = z.nativeEnum(SessionEvidenceType);

export enum EvidenceSeverity {
  PASS = "PASS",
  WARN = "WARN",
  FAIL = "FAIL",
}

export const evidenceSeveritySchema = z.nativeEnum(EvidenceSeverity);

export interface SessionEvidence {
  [key: string]: unknown;
  id: string;
  sessionId: string;
  runId?: string;
  messageEventId?: string;
  messageId?: string;
  testCaseId?: string;
  type: SessionEvidenceType;
  timestamp: string;
  title?: string;
  description?: string;
  severity?: string;
  expected?: unknown;
  actual?: unknown;
  source?: string;
  data: Record<string, unknown>;
  createdBy: string;
  createdAt: string;
}

export const sessionEvidenceSchema: z.ZodSchema<SessionEvidence> = z
  .object({
    id: z.string(),
    sessionId: z.string(),
    runId: z.string().optional(),
    messageEventId: z.string().optional(),
    messageId: z.string().optional(),
    testCaseId: z.string().optional(),
    type: sessionEvidenceTypeSchema,
    timestamp: timestampSchema,
    title: z.string().optional(),
    description: z.string().optional(),
    severity: z.string().optional(),
    expected: z.unknown().optional(),
    actual: z.unknown().optional(),
    source: z.string().optional(),
    data: z.record(z.unknown()),
    createdBy: z.string(),
    createdAt: timestampSchema,
  })
  .passthrough();

export interface SessionSummary {
  generatedCount: number;
  analyzedCount: number;
  passCount: number;
  warnCount: number;
  failCount: number;
  evidenceCount: number;
  vehicleCount: number;
  durationMs: number;
}

export const sessionSummarySchema = z.object({
  generatedCount: z.number().int(),
  analyzedCount: z.number().int(),
  passCount: z.number().int(),
  warnCount: z.number().int(),
  failCount: z.number().int(),
  evidenceCount: z.number().int(),
  vehicleCount: z.number().int(),
  durationMs: z.number(),
});

export interface TestSessionContext {
  sessionId: string;
  projectId: string;
  runId?: string;
  simulationTestId?: string;
  scenarioId?: string;
  messageEventId?: string;
}
